import { create } from 'zustand';
import { config } from '../config/app';
import {
    BASE_SIZE_DEFAULT,
    densityTokens,
    motionTokens, hitTokens,
    SPACING,
    typeTokens,
    FIXED_SHAPE,
    FONTS,
    RADII,
    THEMES,
    type Density,
    type RadiusKey,
    type ThemeName,
    type Tokens,
} from './tokens';

export type ThemeMode = ThemeName | 'system';

/*
    Where the reading pane sits is `readingPos`, and only `readingPos`.

    These used to be two settings that contradicted each other: `layout` had
    `rows` and `list` values meaning "below" and "off", while `readingPos`
    offered the same three choices and was read by nothing at all. `layout` is
    now just how the space is split, and the old values migrate on load.
*/
export type LayoutMode = 'columns' | 'wide';
export type ReadingPos = 'right' | 'bottom' | 'off';

const KEY = 'webmail.ui.v1';

export interface SavedTheme {
    name: string;
    base: ThemeName;
    tokens: Tokens;
    radii: Partial<Record<RadiusKey, number>>;
    baseSize: number;
}

interface Persisted {
    mode: ThemeMode;
    overrides: Record<ThemeName, Tokens>;
    radii: Partial<Record<RadiusKey, number>>;
    baseSize: number;
    density: Density;
    layout: LayoutMode;
    readingPos: ReadingPos;
    saved: SavedTheme[];
    /** Conversation view. The design made this explicitly toggleable. */
    threads: boolean;
    /**
     * Seconds Send waits before it actually submits, so a message can be taken
     * back. Zero sends immediately.
     */
    undoSend: number;
    /** Dragged pane sizes, in px. Separate per orientation — they aren't interchangeable. */
    listWidth: number;
    listHeight: number;
    composeW: number;
    composeH: number;
}

const DEFAULTS: Persisted = {
    mode: 'dark',
    overrides: { dark: {}, light: {} },
    radii: {},
    baseSize: BASE_SIZE_DEFAULT,
    density: 'comfortable',
    layout: 'columns',
    readingPos: 'right',
    saved: [],
    threads: true,
    /*
        Three, not five. The wait is the whole message sitting still — nothing has left, so the
        person is watching a progress bar for a mistake they probably did not make. Long enough to
        catch the flinch that follows the click, short enough not to feel like the app is thinking.
        Five felt like a stall in use. Off and longer are both a setting away.
    */
    undoSend: 3,
    listWidth: 420,
    listHeight: 380,
    composeW: 560,
    composeH: 520,
};

/**
 * Carries a stored `layout` of `rows` or `list` over to the reading-pane
 * setting that now owns that meaning, so nobody's arrangement silently resets.
 */
function migrateLayout(p: Partial<Persisted>): Pick<Persisted, 'layout' | 'readingPos'> {
    const stored = p.layout as string | undefined;
    if (stored === 'rows') return { layout: 'columns', readingPos: 'bottom' };
    if (stored === 'list') return { layout: 'columns', readingPos: 'off' };
    return {
        layout: stored === 'wide' ? 'wide' : 'columns',
        readingPos: p.readingPos ?? DEFAULTS.readingPos,
    };
}

/*
    The accent tokens used to be called --iris, after a product this one is not.

    A person who edited the accent colour has that edit stored under the old name, and a saved
    theme carries a whole map of them. Renaming the tokens without this would not look like a
    rename: it would look like their colours had been quietly thrown away. The old names are
    therefore translated on the way in — from local storage, and from a deployment's brand file
    too, because an operator may have written one before the rename.

    Kept indefinitely rather than for a version or two: it costs four lines and the alternative is
    a day, months from now, when somebody's saved theme comes back grey.
*/
const RENAMED: Record<string, string> = {
    '--iris': '--accent',
    '--iris-strong': '--accent-strong',
    '--iris-bg': '--accent-bg',
    '--on-iris': '--on-accent',
};

export function migrateTokens(tokens: Tokens | undefined): Tokens {
    if (!tokens) return {};
    const out: Tokens = {};
    for (const [key, value] of Object.entries(tokens)) {
        // A new name already present wins: it is the one the person edited most recently.
        const name = RENAMED[key] ?? key;
        if (name in out && RENAMED[key]) continue;
        out[name] = value;
    }
    return out;
}

function load(): Persisted {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return DEFAULTS;
        const p = JSON.parse(raw) as Partial<Persisted>;
        return {
            ...DEFAULTS,
            ...p,
            ...migrateLayout(p),
            overrides: {
                dark: migrateTokens(p.overrides?.dark),
                light: migrateTokens(p.overrides?.light),
            },
            radii: p.radii ?? {},
            saved: (p.saved ?? []).map((theme) => ({ ...theme, tokens: migrateTokens(theme.tokens) })),
        };
    } catch {
        return DEFAULTS;
    }
}

const prefersDark = () =>
    typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)').matches : true;

/*
    Whether the reader has asked for less movement.

    Read from the browser rather than offered as a setting: the person has already answered this
    question, once, for every application on their machine. Asking again in our own preferences
    would be asking them to repeat themselves — and the ones who need it most are the least likely
    to go looking.
*/
const prefersReducedMotion = () =>
    typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)').matches : false;

const coarsePointer = () =>
    typeof matchMedia === 'function' ? matchMedia('(any-pointer: coarse)').matches : false;

const resolve = (mode: ThemeMode): ThemeName =>
    mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode;

interface ThemeState extends Persisted {
    resolved: ThemeName;
    setMode: (mode: ThemeMode) => void;
    setDensity: (density: Density) => void;
    setLayout: (layout: LayoutMode) => void;
    setReadingPos: (pos: ReadingPos) => void;
    setThreads: (on: boolean) => void;
    setUndoSend: (seconds: number) => void;
    setPaneSize: (patch: Partial<Pick<Persisted, 'listWidth' | 'listHeight' | 'composeW' | 'composeH'>>) => void;
    setBaseSize: (px: number) => void;
    setToken: (key: string, value: string) => void;
    clearToken: (key: string) => void;
    setRadius: (key: RadiusKey, px: number) => void;
    revertTheme: () => void;
    editedKeys: () => string[];
    saveTheme: (name: string) => void;
    applyTheme: (name: string) => void;
    deleteTheme: (name: string) => void;
    exportTheme: (name: string) => string;
    importTheme: (json: string) => string | null;
}

function apply(s: Persisted & { resolved: ThemeName }) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const theme = s.resolved;

    root.dataset.theme = theme;
    root.dataset.density = s.density;
    root.dataset.layout = s.layout;
    root.dataset.reading = s.readingPos;
    // Dokunma kuralı bir bayrağın altında yaşıyor, ölçünün sıfır olmasına bırakılmadı — sebebi
    // global.css'te yazılı (sıfır bir min-width masum değildir).
    if (coarsePointer()) root.dataset.coarse = '';
    else delete root.dataset.coarse;

    const base: Tokens = {
        ...THEMES[theme],
        ...FONTS,
        ...FIXED_SHAPE,
        ...densityTokens(s.density, s.baseSize),
        ...typeTokens(s.baseSize),
        ...SPACING,
        ...motionTokens(prefersReducedMotion()),
        ...hitTokens(coarsePointer()),
        // The deployment's brand sits above the built-in theme and below the
        // user's own edits: an operator sets the company's colours, and anyone
        // who dislikes them can still change one token without losing the rest.
        ...migrateTokens(config().tokens),
    };
    for (const [k, v] of Object.entries(base)) root.style.setProperty(k, v);
    for (const r of RADII) root.style.setProperty(r.key, `${s.radii[r.key] ?? r.value}px`);

    // Overrides land last so a half-edited theme wins over the defaults above.
    for (const [k, v] of Object.entries(s.overrides[theme] ?? {})) {
        if (v) root.style.setProperty(k, v);
    }
}

function persist(s: Persisted) {
    try {
        const { mode, overrides, radii, baseSize, density, layout, readingPos, saved, threads, undoSend } = s;
        const { listWidth, listHeight, composeW, composeH } = s;
        localStorage.setItem(
            KEY,
            JSON.stringify({
                mode, overrides, radii, baseSize, density, layout, readingPos, saved, threads, undoSend,
                listWidth, listHeight, composeW, composeH,
            }),
        );
    } catch {
        /* private mode or quota — the UI still works, it just won't remember */
    }
}

export const useTheme = create<ThemeState>((set, get) => {
    const initial = load();
    const commit = (patch: Partial<Persisted>) => {
        const next = { ...get(), ...patch };
        const resolved = resolve(next.mode);
        const state = { ...next, resolved };
        apply(state);
        persist(state);
        set({ ...patch, resolved });
    };

    return {
        ...initial,
        resolved: resolve(initial.mode),

        setMode: (mode) => commit({ mode }),
        setDensity: (density) => commit({ density }),
        setLayout: (layout) => commit({ layout }),
        setReadingPos: (readingPos) => commit({ readingPos }),
        setUndoSend: (undoSend) => commit({ undoSend: Math.max(0, Math.min(30, Math.round(undoSend))) }),
        setThreads: (threads) => commit({ threads }),
        setPaneSize: (patch) => commit(patch),
        setBaseSize: (px) => commit({ baseSize: Math.min(20, Math.max(11, px)) }),

        setToken: (key, value) => {
            const s = get();
            const theme = s.resolved;
            commit({ overrides: { ...s.overrides, [theme]: { ...s.overrides[theme], [key]: value } } });
        },

        clearToken: (key) => {
            const s = get();
            const theme = s.resolved;
            const next = { ...s.overrides[theme] };
            delete next[key];
            commit({ overrides: { ...s.overrides, [theme]: next } });
        },

        setRadius: (key, px) => {
            const s = get();
            commit({ radii: { ...s.radii, [key]: px } });
        },

        revertTheme: () => {
            const s = get();
            commit({ overrides: { ...s.overrides, [s.resolved]: {} }, radii: {} });
        },

        editedKeys: () => Object.keys(get().overrides[get().resolved] ?? {}),

        /** Freezes the current edits under a name. Re-saving the same name replaces it. */
        saveTheme: (name) => {
            const s = get();
            const entry: SavedTheme = {
                name: name.trim() || 'Untitled',
                base: s.resolved,
                tokens: { ...s.overrides[s.resolved] },
                radii: { ...s.radii },
                baseSize: s.baseSize,
            };
            const saved = [...s.saved.filter((t) => t.name !== entry.name), entry];
            commit({ saved });
        },

        applyTheme: (name) => {
            const s = get();
            const entry = s.saved.find((t) => t.name === name);
            if (!entry) return;
            commit({
                mode: entry.base,
                overrides: { ...s.overrides, [entry.base]: { ...entry.tokens } },
                radii: { ...entry.radii },
                baseSize: entry.baseSize,
            });
        },

        deleteTheme: (name) => commit({ saved: get().saved.filter((t) => t.name !== name) }),

        exportTheme: (name) => {
            const entry = get().saved.find((t) => t.name === name);
            return entry ? JSON.stringify(entry, null, 2) : '';
        },

        importTheme: (json) => {
            try {
                const parsed = JSON.parse(json) as Partial<SavedTheme>;
                if (!parsed.name || !parsed.tokens || (parsed.base !== 'dark' && parsed.base !== 'light')) {
                    return 'That does not look like a theme file.';
                }
                const entry: SavedTheme = {
                    name: parsed.name,
                    base: parsed.base,
                    tokens: parsed.tokens,
                    radii: parsed.radii ?? {},
                    baseSize: parsed.baseSize ?? get().baseSize,
                };
                commit({ saved: [...get().saved.filter((t) => t.name !== entry.name), entry] });
                return null;
            } catch {
                return 'That is not valid JSON.';
            }
        },
    };
});

/** Call once at boot, before first paint where possible. */
export function initTheme() {
    const s = useTheme.getState();
    apply(s);
    if (typeof matchMedia === 'function') {
        matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
            if (useTheme.getState().mode === 'system') useTheme.getState().setMode('system');
        });
        // Somebody can turn movement off while the page is open — in the operating system, not
        // here — and the answer has to take effect without a reload.
        matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
            apply({ ...useTheme.getState() });
        });
        // Aynı sebeple: cihaza dokunmatik ekran takılabilir, tablet klavyeye oturtulabilir.
        matchMedia('(any-pointer: coarse)').addEventListener('change', () => {
            apply({ ...useTheme.getState() });
        });
    }
}
